import { useEffect, useState } from 'react';
import api, { fmtRp, errMsg, branchLabel } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, ScanBarcode, Search, Trash2, PackagePlus, X } from 'lucide-react';
import BarcodeScanner from '../components/BarcodeScanner';

const emptyVariant = () => ({ size: '', barcode: '', modal: '', price: '', qty: '' });

export default function Stok() {
  const { user, branches } = useAuth();
  const isAdmin = user.role === 'admin';
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [restockProduct, setRestockProduct] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanTarget, setScanTarget] = useState('lookup');
  const [form, setForm] = useState({ article: '', brand: '', category: 'sepatu', branch: isAdmin ? (branches[0]?.id || 'balaraja') : user.branch, variants: [emptyVariant()] });
  const [restock, setRestock] = useState({ size: '', qty: '', branch: isAdmin ? (branches[0]?.id || 'balaraja') : user.branch });

  const load = () => api.get('/products', { params: { search } }).then((r) => setProducts(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    const t = setTimeout(load, 300);
    const iv = setInterval(load, 10000);
    return () => { clearTimeout(t); clearInterval(iv); };
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const openScanner = (target) => {
    setScanTarget(target);
    setScanOpen(true);
  };

  const handleScan = async (code) => {
    if (scanTarget === 'lookup') {
      try {
        const { data } = await api.get(`/products/barcode/${code}`);
        setRestockProduct(data.product);
        setRestock({ size: data.size || '', qty: '', branch: isAdmin ? (branches[0]?.id || 'balaraja') : user.branch });
        toast.success(`Produk ditemukan: ${data.product.article} uk ${data.size}`);
      } catch {
        toast.error('Barcode tidak dikenal. Tambahkan produk baru terlebih dahulu.');
      }
    } else if (scanTarget.startsWith('variant-')) {
      const idx = Number(scanTarget.split('-')[1]);
      setForm((f) => ({ ...f, variants: f.variants.map((v, i) => (i === idx ? { ...v, barcode: code } : v)) }));
      toast.success(`Barcode ${code} tersimpan`);
    } else if (scanTarget === 'restock-barcode') {
      setRestock((r) => ({ ...r, barcode: code }));
      toast.success(`Barcode ${code} tersimpan`);
    }
  };

  const submitProduct = async () => {
    if (!form.article.trim()) return toast.error('Nama artikel wajib diisi');
    const variants = form.variants.filter((v) => v.size.trim());
    if (variants.length === 0) return toast.error('Minimal 1 ukuran');
    try {
      await api.post('/products', {
        article: form.article, brand: form.brand, category: form.category,
        variants: variants.map((v) => ({
          size: v.size, barcode: v.barcode,
          modal: Number(v.modal) || 0, price: Number(v.price) || 0,
          stock_branch: form.branch, stock_qty: Number(v.qty) || 0,
        })),
      });
      toast.success('Produk berhasil ditambahkan');
      setAddOpen(false);
      setForm({ article: '', brand: '', category: 'sepatu', branch: isAdmin ? (branches[0]?.id || 'balaraja') : user.branch, variants: [emptyVariant()] });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const submitRestock = async () => {
    if (!restock.size) return toast.error('Pilih ukuran');
    if (!(Number(restock.qty) > 0)) return toast.error('Jumlah harus lebih dari 0');
    try {
      await api.post(`/products/${restockProduct.id}/restock`, {
        size: restock.size, branch: restock.branch, qty: Number(restock.qty),
        barcode: restock.barcode || null,
      });
      toast.success(`Stok ${restockProduct.article} uk ${restock.size} +${restock.qty} psg (${branchLabel(restock.branch)})`);
      setRestockProduct(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const deleteProduct = async (p) => {
    try {
      await api.delete(`/products/${p.id}`);
      toast.success('Produk dihapus');
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const rows = products.flatMap((p) => p.variants.map((v) => ({ p, v })));

  return (
    <div className="space-y-5 animate-fade-up" data-testid="stok-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Stok Barang</h2>
          <p className="text-sm text-muted-foreground">Tambah produk & stok, manual atau scan barcode</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => openScanner('lookup')} data-testid="stok-scan-button"
            className="px-4 py-2.5 rounded-xl bg-elevated border border-line hover:border-brand hover:text-brand text-sm font-semibold flex items-center gap-2 transition-colors">
            <ScanBarcode className="w-4 h-4" /> Scan
          </button>
          <button onClick={() => setAddOpen(true)} data-testid="add-product-button"
            className="px-4 py-2.5 rounded-xl bg-brand hover:bg-branddark text-white text-sm font-bold flex items-center gap-2 shadow-lg shadow-brand/25 transition-all active:scale-[0.98]">
            <Plus className="w-4 h-4" /> Tambah Produk
          </button>
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari artikel / merk / barcode..." className="lodi-input pl-9" data-testid="stok-search-input" />
      </div>

      <div className="lodi-card overflow-hidden" data-testid="stok-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground uppercase tracking-wide border-b border-line bg-elevated/50">
                <th className="px-4 py-3">Artikel</th>
                <th className="px-4 py-3">Ukuran</th>
                <th className="px-4 py-3">Barcode</th>
                <th className="px-4 py-3 text-right">Modal</th>
                <th className="px-4 py-3 text-right">Harga</th>
                {isAdmin ? (
                  branches.map((b) => <th key={b.id} className="px-4 py-3 text-center">{b.name}</th>)
                ) : (
                  <th className="px-4 py-3 text-center">Stok</th>
                )}
                <th className="px-4 py-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={isAdmin ? 6 + branches.length : 7} className="px-4 py-10 text-center text-muted-foreground" data-testid="stok-empty">Belum ada produk</td></tr>
              )}
              {rows.map(({ p, v }, i) => (
                <tr key={`${p.id}-${v.size}-${i}`} className="border-b border-line/50 last:border-0 hover:bg-elevated/40 transition-colors" data-testid={`stok-row-${p.id}-${v.size}`}>
                  <td className="px-4 py-3">
                    <p className="font-semibold">{p.article}</p>
                    <p className="text-xs text-muted-foreground">{p.brand} • {p.category}</p>
                  </td>
                  <td className="px-4 py-3 font-mono font-semibold">{v.size}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{v.barcode || '-'}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">{isAdmin ? fmtRp(v.modal) : '***'}</td>
                  <td className="px-4 py-3 text-right font-semibold text-emerald-400">{fmtRp(v.price)}</td>
                  {isAdmin ? (
                    branches.map((b) => (
                      <td key={b.id} className="px-4 py-3 text-center"><span className={`font-bold ${(v.stock?.[b.id] ?? 0) <= 2 ? 'text-brand' : ''}`}>{v.stock?.[b.id] ?? 0}</span></td>
                    ))
                  ) : (
                    <td className="px-4 py-3 text-center"><span className={`font-bold ${(v.stock?.[user.branch] ?? 0) <= 2 ? 'text-brand' : ''}`}>{v.stock?.[user.branch] ?? 0}</span></td>
                  )}
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1.5">
                      <button onClick={() => { setRestockProduct(p); setRestock({ size: v.size, qty: '', branch: isAdmin ? (branches[0]?.id || 'balaraja') : user.branch }); }}
                        data-testid={`restock-${p.id}-${v.size}`} title="Tambah stok"
                        className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 flex items-center justify-center hover:bg-emerald-500/20 transition-colors">
                        <PackagePlus className="w-4 h-4" />
                      </button>
                      {isAdmin && (
                        <button onClick={() => deleteProduct(p)} data-testid={`delete-product-${p.id}`} title="Hapus produk"
                          className="w-8 h-8 rounded-lg bg-brand/10 border border-brand/25 text-brand flex items-center justify-center hover:bg-brand/20 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-surface border-line max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="add-product-dialog">
          <DialogHeader><DialogTitle className="font-display">Tambah Produk Baru</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <span className="lodi-label">Nama Artikel *</span>
              <input value={form.article} onChange={(e) => setForm({ ...form, article: e.target.value })} className="lodi-input" placeholder="cth: New Era 9FORTY / Sneakers Runner" data-testid="product-article-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="lodi-label">Merk</span>
                <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="lodi-input" placeholder="cth: New Era" data-testid="product-brand-input" />
              </div>
              <div>
                <span className="lodi-label">Kategori</span>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="lodi-input" data-testid="product-category-select">
                  <option value="sepatu">Sepatu</option>
                  <option value="sandal">Sandal</option>
                  <option value="lainnya">Lainnya</option>
                </select>
              </div>
            </div>
            <div>
              <span className="lodi-label">Stok Awal Masuk Ke Cabang</span>
              {isAdmin ? (
                <div className="flex flex-wrap gap-2">
                  {branches.map((b) => (
                    <button key={b.id} onClick={() => setForm({ ...form, branch: b.id })} data-testid={`product-branch-${b.id}`}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold border ${form.branch === b.id ? 'bg-brand text-white border-brand' : 'bg-elevated border-line text-muted-foreground'}`}>
                      {b.name}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm font-semibold">{branchLabel(user.branch)}</p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="lodi-label mb-0">Varian Ukuran *</span>
                <button onClick={() => setForm({ ...form, variants: [...form.variants, emptyVariant()] })} data-testid="add-variant-row"
                  className="text-xs font-bold text-brand hover:underline flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Tambah Ukuran</button>
              </div>
              <div className="hidden sm:grid grid-cols-12 gap-3 px-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground" data-testid="variant-header">
                <span className="col-span-2">Ukuran *</span>
                <span className="col-span-3">Barcode</span>
                <span className="col-span-2">Modal (Rp)</span>
                <span className="col-span-2">Harga Jual (Rp)</span>
                <span className="col-span-2">Jumlah (psg)</span>
                <span className="col-span-1" />
              </div>
              <div className="space-y-2">
                {form.variants.map((v, i) => (
                  <div key={i} className="grid grid-cols-2 sm:grid-cols-12 gap-3 items-end bg-elevated rounded-lg p-3" data-testid={`variant-row-${i}`}>
                    <div className="col-span-1 sm:col-span-2">
                      <span className="lodi-label sm:hidden">Ukuran *</span>
                      <input value={v.size} onChange={(e) => setForm({ ...form, variants: form.variants.map((x, xi) => xi === i ? { ...x, size: e.target.value } : x) })}
                        placeholder="42" className="lodi-input h-11" data-testid={`variant-size-${i}`} />
                    </div>
                    <div className="col-span-1 sm:col-span-3">
                      <span className="lodi-label sm:hidden">Barcode</span>
                      <div className="flex gap-1.5">
                        <input value={v.barcode} onChange={(e) => setForm({ ...form, variants: form.variants.map((x, xi) => xi === i ? { ...x, barcode: e.target.value } : x) })}
                          placeholder="Scan / ketik" className="lodi-input h-11" data-testid={`variant-barcode-${i}`} />
                        <button type="button" onClick={() => openScanner(`variant-${i}`)} data-testid={`variant-scan-${i}`}
                          className="shrink-0 w-11 h-11 rounded-lg bg-surface border border-line flex items-center justify-center text-muted-foreground hover:text-brand hover:border-brand">
                          <ScanBarcode className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="col-span-1 sm:col-span-2">
                      <span className="lodi-label sm:hidden">Modal (Rp)</span>
                      <input type="number" value={v.modal} onChange={(e) => setForm({ ...form, variants: form.variants.map((x, xi) => xi === i ? { ...x, modal: e.target.value } : x) })}
                        placeholder="150000" className="lodi-input h-11" data-testid={`variant-modal-${i}`} />
                    </div>
                    <div className="col-span-1 sm:col-span-2">
                      <span className="lodi-label sm:hidden">Harga Jual (Rp)</span>
                      <input type="number" value={v.price} onChange={(e) => setForm({ ...form, variants: form.variants.map((x, xi) => xi === i ? { ...x, price: e.target.value } : x) })}
                        placeholder="250000" className="lodi-input h-11" data-testid={`variant-price-${i}`} />
                    </div>
                    <div className="col-span-1 sm:col-span-2">
                      <span className="lodi-label sm:hidden">Jumlah (psg)</span>
                      <input type="number" value={v.qty} onChange={(e) => setForm({ ...form, variants: form.variants.map((x, xi) => xi === i ? { ...x, qty: e.target.value } : x) })}
                        placeholder="10" className="lodi-input h-11" data-testid={`variant-qty-${i}`} />
                    </div>
                    <div className="col-span-2 sm:col-span-1 flex justify-end">
                      <button onClick={() => setForm({ ...form, variants: form.variants.filter((_, xi) => xi !== i) })} disabled={form.variants.length === 1}
                        className="w-11 h-11 rounded-lg bg-surface border border-line text-muted-foreground hover:text-brand hover:border-brand disabled:opacity-30 flex items-center justify-center" data-testid={`variant-remove-${i}`}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button onClick={submitProduct} data-testid="submit-product-button"
              className="w-full py-3 rounded-xl bg-brand hover:bg-branddark text-white font-display font-bold transition-all active:scale-[0.98]">
              SIMPAN PRODUK
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!restockProduct} onOpenChange={(v) => !v && setRestockProduct(null)}>
        <DialogContent className="bg-surface border-line max-w-sm" data-testid="restock-dialog">
          <DialogHeader><DialogTitle className="font-display">Tambah Stok — {restockProduct?.article}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <span className="lodi-label">Ukuran *</span>
              <select value={restock.size} onChange={(e) => setRestock({ ...restock, size: e.target.value })} className="lodi-input" data-testid="restock-size-select">
                <option value="">Pilih ukuran</option>
                {restockProduct?.variants.map((v) => <option key={v.size} value={v.size}>{v.size}</option>)}
              </select>
            </div>
            <div>
              <span className="lodi-label">Cabang</span>
              {isAdmin ? (
                <div className="flex flex-wrap gap-2">
                  {branches.map((b) => (
                    <button key={b.id} onClick={() => setRestock({ ...restock, branch: b.id })} data-testid={`restock-branch-${b.id}`}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold border ${restock.branch === b.id ? 'bg-brand text-white border-brand' : 'bg-elevated border-line text-muted-foreground'}`}>
                      {b.name}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm font-semibold">{branchLabel(user.branch)}</p>
              )}
            </div>
            <div>
              <span className="lodi-label">Jumlah (pasang) *</span>
              <input type="number" min="1" value={restock.qty} onChange={(e) => setRestock({ ...restock, qty: e.target.value })} className="lodi-input" data-testid="restock-qty-input" />
            </div>
            <p className="text-xs text-muted-foreground bg-elevated border border-line rounded-lg px-3 py-2">Modal & harga jual otomatis mengikuti data produk yang sudah ada — tidak perlu diisi ulang.</p>
            <div>
              <span className="lodi-label">Barcode (opsional)</span>
              <div className="flex gap-2">
                <input value={restock.barcode || ''} onChange={(e) => setRestock({ ...restock, barcode: e.target.value })} className="lodi-input" placeholder="Scan / ketik" data-testid="restock-barcode-input" />
                <button onClick={() => openScanner('restock-barcode')} className="shrink-0 w-11 rounded-lg bg-elevated border border-line flex items-center justify-center text-muted-foreground hover:text-brand hover:border-brand" data-testid="restock-scan-button">
                  <ScanBarcode className="w-4 h-4" />
                </button>
              </div>
            </div>
            <button onClick={submitRestock} data-testid="submit-restock-button"
              className="w-full py-3 rounded-xl bg-brand hover:bg-branddark text-white font-display font-bold transition-all active:scale-[0.98]">
              TAMBAH STOK
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={handleScan} title="Scan Barcode" />
    </div>
  );
}
